'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';

import { localizeHref } from '@/i18n/localize-href';
import type { Locale } from '@/i18n/locales';
import { COMPARE_NAV, PRIMARY_NAV_LINKS, getFreeToolNavGroups } from '@/lib/marketing-nav';
import { Button } from './button';
import { MobileNav } from './mobile-nav';
import { NavMenuDropdown } from './nav-menu-dropdown';

export function Navigation() {
  const t = useTranslations();
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  const locale = useLocale() as Locale;
  const href = (path: string) => localizeHref(path, locale);

  // `label` stays the English fallback for links that intentionally have no
  // messageKey (comparison/tool pages that stay English — see ui-copy.ts).
  const label = (item: { label: string; messageKey?: string }) =>
    item.messageKey ? t(item.messageKey) : item.label;

  return (
    <nav
      className="sticky top-0 z-50 w-full pt-4 md:pt-6 px-4 sm:px-6 lg:px-8"
      aria-label={t('ui.nav.ariaMain')}
    >
      <div className="mx-auto max-w-7xl w-full">
        <div className="relative flex h-16 items-center justify-between bg-black rounded-full px-4 sm:px-6 shadow-lg border border-white/10">
          <div className="absolute -inset-[1px] rounded-full bg-gradient-to-r from-[#EC4899] via-[#8B5CF6] to-[#F59E0B] opacity-20 blur-sm -z-10" />
          <div className="absolute -inset-[2px] rounded-full bg-gradient-to-r from-[#EC4899] via-[#8B5CF6] to-[#F59E0B] opacity-10 blur-md -z-20" />

          <Link href={href('/')} className="relative z-10 flex items-center justify-center gap-1">
            <Image
              src="/logos/wisprs-logo.png"
              alt="Wisprs"
              width={56}
              height={42}
              className="flex-shrink-0 object-contain mt-1"
            />
            <span
              className="text-xl md:text-2xl font-black text-white leading-none tracking-tight"
              style={{ fontFamily: 'var(--font-fredoka)', fontWeight: 900 }}
            >
              wisprs
            </span>
          </Link>

          <div className="relative z-10 hidden md:flex items-center space-x-8">
            {PRIMARY_NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={href(link.href)}
                prefetch={link.prefetch ?? true}
                className="text-base font-bold text-white/90 hover:text-white transition-colors"
              >
                {label(link)}
              </Link>
            ))}
            <NavMenuDropdown
              label={t('ui.nav.compare')}
              items={COMPARE_NAV.featured}
              hubLink={{ ...COMPARE_NAV.hub, label: t('ui.nav.compareHub') }}
              ariaLabel={t('ui.nav.compareMenuAria')}
            />
            <NavMenuDropdown
              label={t('ui.nav.freeTools')}
              groups={getFreeToolNavGroups()}
              hubLink={{ href: '/tools', label: t('ui.nav.freeToolsHub'), prefetch: false }}
              ariaLabel={t('ui.nav.freeToolsMenuAria')}
              // Width, height cap, and side-flipping all live in NavMenuDropdown.
            />
          </div>

          <div className="relative z-10 flex items-center gap-2 sm:gap-3">
            <MobileNav />
            <div className="hidden md:flex items-center gap-3">
              <Link href={href('/sign-in')}>
                <Button
                  variant="secondary"
                  size="md"
                  className="h-10 px-4 text-sm font-medium bg-transparent border border-white/20 hover:border-white/40 text-white hover:bg-white/5"
                >
                  {t('nav.login')}
                </Button>
              </Link>
              <Link href={href('/sign-up')}>
                <Button
                  variant="primary"
                  size="md"
                  className="h-10 px-5 text-sm font-semibold bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] hover:from-[#7C3AED] hover:via-[#DB2777] hover:to-[#D97706] text-white border-0 min-w-[140px] sm:min-w-[180px]"
                >
                  {t('nav.primaryCta')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
